/**
 * The ranked layout of the allocation workbench — the default view since 2026-11-03.
 *
 * Same rows, same item and allocation cells, same offer cards and the same click handlers as the
 * matrix (`WorkbenchTable`); only the middle changes. Instead of one column per supplier — on a
 * 300-item, 20-supplier enquiry a row of mostly dashes the purchaser scrolls sideways past on
 * every item — each row lists just the suppliers who quoted it, cheapest first.
 *
 * Above the table, a coverage strip: how many items each supplier quoted, is cheapest on, and has
 * been picked for. The purchaser consolidates to save delivery charges, so "who can take most of
 * this list" is half the decision. Clicking a supplier outlines their offer on every row.
 *
 * Ranking and coverage are `lib/rankedOffers.ts`; nothing here computes a price.
 */

import { useMemo, useState, type ComponentProps } from 'react'
import { OfferCard } from './OfferCell'
import { AllocationCell, ItemCell } from './RowCells'
import type { WorkbenchTable } from './WorkbenchTable'
import { rowsForItem, type NormalisedItem } from '../../lib/compare'
import { MAX_COLLAPSED_OFFERS, rankOffers, supplierCoverage } from '../../lib/rankedOffers'

export function RankedTable({
  allItems,
  ...props
}: ComponentProps<typeof WorkbenchTable> & {
  /**
   * The whole enquiry, before the item search. Coverage is about the whole list — "Marine Supply
   * quoted 212 of 300" — and should not shrink to "3 of 3" because someone searched for rice.
   */
  allItems: NormalisedItem[]
}) {
  const { items, visibleSuppliers, supplierName, allocations, baseCurrency, display } = props
  const [highlighted, setHighlighted] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())

  const coverage = useMemo(
    () => supplierCoverage(allItems, visibleSuppliers, allocations),
    [allItems, visibleSuppliers, allocations],
  )
  // A supplier hidden by the filter can't stay highlighted — its outline would point at nothing.
  const activeHighlight = highlighted && visibleSuppliers.includes(highlighted) ? highlighted : null

  return (
    <>
      {coverage.length > 0 ? (
        <div className="covstrip" aria-label="Supplier coverage">
          {coverage.map((c) => (
            <button
              key={c.supplier}
              type="button"
              className={`covcard${activeHighlight === c.supplier ? ' on' : ''}`}
              aria-pressed={activeHighlight === c.supplier}
              title="Outline this supplier's offer on every item — to see where one supplier could take more of the list"
              onClick={() => setHighlighted((cur) => (cur === c.supplier ? null : c.supplier))}
            >
              <b>{supplierName.get(c.supplier) ?? c.supplier}</b>
              <span className="s">
                {c.quoted} / {allItems.length} quoted · {c.cheapest} cheapest
              </span>
              <span className="s">{c.picked} picked</span>
              <span className="bar">
                <i style={{ width: `${allItems.length ? (c.quoted / allItems.length) * 100 : 0}%` }} />
              </span>
            </button>
          ))}
        </div>
      ) : null}

      <div className="wb">
        <table className="wbt ranked">
          <thead>
            <tr>
              <th className="sticky">Item</th>
              <th className="right">Qty</th>
              <th>Quotes — cheapest first</th>
              <th className="sum right">Allocation</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ color: 'var(--muted)', padding: 22, textAlign: 'center' }}>
                  No items match the filter.
                </td>
              </tr>
            ) : null}

            {items.map((item) => {
              const key = `${item.item_code}::${item.enquiry_row_no}`
              const allocation = rowsForItem(allocations, item)
              const allocatedTo = (supplier: string) =>
                allocation.find((row) => row.supplier === supplier)?.allocated_qty ?? 0
              const ranked = rankOffers(item, visibleSuppliers)
              const open = expanded.has(key)
              // Collapsed, a row still shows the picked offer and the highlighted supplier's even
              // when they rank below the cut — the decision and the question are never hidden.
              const shown = open
                ? ranked
                : ranked.filter(
                    (r) =>
                      r.rank <= MAX_COLLAPSED_OFFERS ||
                      allocatedTo(r.offer.supplier) > 0 ||
                      r.offer.supplier === activeHighlight,
                  )
              const hiddenCount = ranked.length - shown.length

              return (
                <tr key={key}>
                  <ItemCell
                    item={item}
                    supplierName={supplierName}
                    onPriceLine={props.onPriceLine}
                    onUseOurStock={props.onUseOurStock}
                    inHouseFor={props.inHouseFor}
                  />

                  <td className="right mono">
                    {item.qty}
                    <div style={{ color: 'var(--muted)', fontSize: 11 }}>{item.uom}</div>
                  </td>

                  <td className="quotes">
                    {ranked.length === 0 ? (
                      <span style={{ color: 'var(--danger)', fontWeight: 600, fontSize: 12 }}>
                        No supplier quoted
                        {item.excluded.length > 0 ? ' a usable price' : ''}
                      </span>
                    ) : (
                      <div className="rankchips">
                        {shown.map((r) => (
                          <OfferCard
                            key={r.offer.supplier}
                            offer={r.offer}
                            requiredQty={item.qty}
                            allocatedQty={allocatedTo(r.offer.supplier)}
                            isBest={r.isBest}
                            baseCurrency={baseCurrency}
                            display={display}
                            onToggle={() => props.onToggleCell(item.item_code, item.enquiry_row_no, r.offer.supplier)}
                            onSetQty={(qty) => props.onSetQty(item.item_code, item.enquiry_row_no, r.offer.supplier, qty)}
                            ranked={{
                              supplierName: supplierName.get(r.offer.supplier) ?? r.offer.supplier,
                              rank: r.rank,
                              premiumPct: r.premiumPct,
                              highlighted: r.offer.supplier === activeHighlight,
                            }}
                          />
                        ))}
                        {hiddenCount > 0 || (open && ranked.length > MAX_COLLAPSED_OFFERS) ? (
                          <button
                            type="button"
                            className="linkbtn"
                            onClick={() =>
                              setExpanded((cur) => {
                                const next = new Set(cur)
                                if (next.has(key)) next.delete(key)
                                else next.add(key)
                                return next
                              })
                            }
                          >
                            {open ? 'show fewer' : `+${hiddenCount} more`}
                          </button>
                        ) : null}
                      </div>
                    )}
                  </td>

                  <AllocationCell
                    item={item}
                    allocation={allocation}
                    supplierName={supplierName}
                    baseCurrency={baseCurrency}
                    display={display}
                    onAutoCover={props.onAutoCover}
                  />
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
